import React from 'react';
import { motion } from 'framer-motion';
import { Users, Zap, Shield, Clock } from 'lucide-react';

const reasons = [
  {
    icon: Users,
    title: "Built for Local Business",
    description: "We understand the unique challenges of small and medium businesses. No enterprise jargon, just practical AI solutions that fit your scale and budget."
  },
  {
    icon: Zap,
    title: "Fast Implementation",
    description: "Most projects go from kickoff to production in weeks, not months. We move fast so you see results quickly."
  },
  {
    icon: Shield,
    title: "Your Data Stays Yours",
    description: "We take data privacy seriously. Your business data is handled with care and never shared or used for other purposes."
  },
  {
    icon: Clock,
    title: "Ongoing Support",
    description: "AI isn't set-and-forget. We provide ongoing support and optimization to ensure your AI keeps delivering value."
  }
];

export default function WhySection() {
  return (
    <section className="bg-slate-900 py-24 md:py-32">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <span className="text-amber-400 text-sm font-semibold tracking-widest uppercase">Why Choose Us</span>
          <h2 className="mt-4 text-3xl md:text-5xl font-bold text-white tracking-tight">
            AI that actually works for you
          </h2>
          <p className="mt-4 text-slate-400 text-lg max-w-2xl mx-auto">
            We're not a faceless tech company. We're your partner in making AI work for your business.
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 gap-8">
          {reasons.map((reason, i) => (
            <motion.div
              key={reason.title}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.1, duration: 0.5 }}
              className="flex gap-5 p-6 rounded-2xl bg-slate-800/30 border border-slate-800 hover:border-slate-700 transition-colors"
            >
              <div className="flex-shrink-0">
                <div className="p-3 rounded-xl bg-amber-500/10 border border-amber-500/20">
                  <reason.icon className="w-6 h-6 text-amber-400" />
                </div>
              </div>
              <div>
                <h3 className="text-xl font-semibold text-white mb-2">{reason.title}</h3>
                <p className="text-slate-400 leading-relaxed">{reason.description}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
